import { io } from "socket.io-client";
import { getDeviceId, splitSocketUrl } from "../utils";
export class UpdateStream {
    constructor(socketUrl, config, reconnectionDelay = 2000) {
        /**
         * Addition flag to show if socket is connecting/reconnecting
         */
        this.connecting = false;
        /**
         * Counter for retries
         */
        this.retries = 0;
        this.initialize = () => {
            if (this.socket) {
                this.config.debug && console.debug("UpdateStream: Already initialized.");
                return this.socket;
            }
            this.connecting = true;
            const { baseUrl, urlPath } = splitSocketUrl(this.socketUrl);
            const socketConfig = {
                reconnectionDelay: this.reconnectionDelay,
                reconnection: !!this.config.autoReconnect,
                transports: ["websocket"],
                upgrade: false,
            };
            if (urlPath) {
                socketConfig.path = urlPath;
            }
            return (this.socket = io(baseUrl, socketConfig)
                .on("connect", this.handleConnect)
                .on("error", this.handleError)
                .on("connect_error", this.handleConnectError)
                .on("disconnect", this.handleDisconnect)
                .on("authorized", this.handleAuthorized)
                .on("update", this.handleUpdate));
        };
        this.setConfig = (config) => {
            // reset retires if token changes
            if (this.config.token !== config.token) {
                this.retries = 0;
            }
            this.config = config;
        };
        this.disconnect = () => this.socket.disconnect();
        this.connect = () => {
            if (this.socket.connected) {
                // eslint-disable-next-line no-console
                this.config.debug && console.debug("UpdateStream: Already connected.");
                return;
            }
            // eslint-disable-next-line no-console
            this.config.debug && console.debug("UpdateStream: Try to connect...");
            this.connecting = true;
            this.socket.connect();
        };
        this.handleError = (error) => {
            console.error("UpdateStream: An error occurred.");
            this.config.debug && console.error(error);
            this.config.onError && this.config.onError(error);
        };
        this.handleConnectError = (error) => {
            console.error("UpdateStream: A connect error occurred.");
            this.config.debug && console.error(error);
            this.config.onError && this.config.onError(error);
            if (!this.config.autoReconnect) {
                this.handleDisconnect(error.message, error);
            }
        };
        this.handleConnect = () => {
            const deviceId = getDeviceId(this.config.projectId);
            // eslint-disable-next-line no-console
            this.config.debug &&
                console.debug(`UpdateStream: Socket connected [deviceId ${deviceId}]. send ${this.socket.sendBuffer.length} buffered messages.`);
            this.connecting = false;
            this.config.onConnect && this.config.onConnect(deviceId);
            // Authorize user at the socket server (the token is used only once for the signup call at the REST API)
            this.socket.emit("authorize", {
                token: this.config.token,
                deviceId,
                apiUrl: this.config.apiUrl,
            });
        };
        this.handleDisconnect = (reason, details) => {
            // eslint-disable-next-line no-console
            this.config.debug && console.debug("UpdateStream: Disconnected.", details);
            // By default, socket.io client tries to reconnect on disconnect, except of the following two reasons
            // see: https://socket.io/docs/v4/client-api/#event-disconnect
            this.connecting =
                !!this.config.autoReconnect &&
                    !(reason === "io server disconnect" || reason === "io client disconnect");
            this.config.onDisconnect && this.config.onDisconnect();
            if (this.connecting) {
                // eslint-disable-next-line no-console
                this.config.debug && console.debug("UpdateStream: auto-reconnecting...");
            }
            else {
                if (this.retries > 20) {
                    console.error("UpdateStream: Too many retries. Please reload the page.");
                    this.config.onTooManyRetries && this.config.onTooManyRetries();
                    return;
                }
                // try to reconnect after some delay
                setTimeout(() => {
                    try {
                        // eslint-disable-next-line no-console
                        this.config.debug &&
                            console.debug(`UpdateStream: Will reconnect... (try ${this.retries})`);
                        this.retries++;
                        this.connect();
                    }
                    catch (e) {
                        // eslint-disable-next-line no-console
                        this.config.debug &&
                            console.debug("UpdateStream: Could not reconnect. Will reinitialize...");
                        this.retries = 0;
                        this.initialize();
                    }
                }, this.reconnectionDelay * Math.max(1, this.retries));
            }
        };
        this.handleAuthorized = (deviceId) => {
            // eslint-disable-next-line no-console
            this.config.debug &&
                console.debug(`UpdateStream: User authorized [deviceId ${deviceId}].`);
            this.config.onAuthorize && this.config.onAuthorize(deviceId);
        };
        this.handleUpdate = (message) => {
            // eslint-disable-next-line no-console
            this.config.debug &&
                console.debug("UpdateStream: Received message.", message);
            this.config.onMessage(message);
        };
        this.config = config;
        this.socketUrl = socketUrl;
        this.reconnectionDelay = reconnectionDelay;
        this.socket = this.initialize();
    }
}
